import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useAsyncData } from "@/hooks/useAsyncData";
import { getBlogBySlug } from "@/data/repository";
import { ErrorState } from "@/components/shared/DataState";
import CTASection from "@/components/sections/CTASection";
import { ArrowLeft, Newspaper } from "lucide-react";

function BlogSkeleton() {
  return (
    <div className="container-page py-16 animate-pulse" data-testid="loading-state">
      <div className="h-4 w-28 bg-bronze/10 mb-8" />
      <div className="h-10 w-3/4 bg-bronze/15 mb-4" />
      <div className="aspect-[16/9] bg-bronze/10 mt-6" />
      <div className="mt-8 space-y-3">
        <div className="h-4 w-full bg-bronze/10" />
        <div className="h-4 w-5/6 bg-bronze/10" />
        <div className="h-4 w-4/6 bg-bronze/10" />
      </div>
    </div>
  );
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const { data: blog, loading, error, reload } = useAsyncData(
    () => getBlogBySlug(slug),
    [slug],
    null
  );

  if (loading) return <BlogSkeleton />;
  if (error)
    return (
      <div className="container-page py-16">
        <ErrorState onRetry={reload} />
      </div>
    );

  if (!blog) {
    return (
      <div className="container-page py-24 text-center" data-testid="blog-not-found">
        <Newspaper className="mx-auto text-bronze" size={36} />
        <h1 className="font-serif text-4xl text-ink mt-5">Article not found</h1>
        <p className="text-ink_soft/70 mt-3">This article may have been moved or unpublished.</p>
        <Link to="/resources/blogs" className="btn-primary mt-7 inline-block">
          Back to Blogs
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="page-blog-detail" className="bg-cream">
      <article className="container-page py-10 md:py-16 max-w-3xl">
        <Link
          to="/resources/blogs"
          data-testid="back-to-blogs"
          className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase text-bronze hover:text-bronze_dark"
        >
          <ArrowLeft size={14} /> All Articles
        </Link>

        <div className="mt-7 flex items-center gap-3 text-[0.7rem] tracking-[0.22em] uppercase text-olive">
          <span className="text-bronze">{blog.category}</span>
          <span>·</span>
          <span>{blog.date}</span>
          <span>·</span>
          <span>{blog.readTime}</span>
        </div>

        <h1 className="mt-4 font-serif text-4xl md:text-5xl text-ink leading-tight">
          {blog.title}
        </h1>

        {blog.image && (
          <div className="mt-8 aspect-[16/9] overflow-hidden bg-beige">
            <img src={blog.image} alt={blog.title} className="h-full w-full object-cover" />
          </div>
        )}

        <div
          className="prose-editorial mt-10 text-base font-light leading-relaxed text-ink_soft/85 space-y-5"
          data-testid="blog-body"
          // Blog body is admin-authored HTML; sanitize on render as defence-in-depth.
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blog.body || `<p>${blog.excerpt || ""}</p>`) }}
        />
      </article>
      <CTASection />
    </div>
  );
}
